import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import Loading from "../loading";
import FormSection from "./FormSection";
import { useSchoolFeeCatalogue } from "../../api/queries";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { getFailureDisplay, getStorageToken } from "../../utils";
import { SchoolFeeCataloguePlacement } from "../../models/schoolFee";

// The server CLAMPS size to 100 silently, so asking for more is asking for 100
// and being told nothing about it.
export const CATALOGUE_PAGE_SIZE = 100;

// Q4 condition 2. A school with more placements than this is a configuration
// we refuse to render rather than truncate: a payer cannot tell a short list
// from a wrong one, and the class they are looking for is the one missing.
export const MAX_CATALOGUE_PAGES = 20;

// First-seen order, because the server's order is the only order anyone has
// agreed on. Sorting here would silently reorder a school's academic periods.
const uniqueInOrder = (values: string[]): string[] => {
  const seen = new Set<string>();

  return values.filter(value => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

/**
 * One rung of the cascade. A single option is rendered read-only and chosen
 * for the payer -- approved 2026-08-27, with no explanation shown: a school
 * with one academic period does not need to be told it has one.
 */
const PlacementSelect = ({
  id,
  label,
  placeholder,
  value,
  options,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  if (!disabled && options.length === 1) {
    // A value in a quiet box, drawn as the same outlined field as the selects
    // beside it so its label notches the border at the same height and the row
    // stays aligned -- a label stacked above a free-standing box sat a line
    // lower and made this field read as a different control.
    return (
      <TextInput
        fullWidth
        id={id}
        data-testid={`${id}-fixed`}
        label={label}
        value={options[0]}
        variant="outlined"
        sx={{ "& .MuiInputBase-input": { fontWeight: 600 } }}
        InputProps={{ readOnly: true, sx: { bgcolor: "grey.100" } }}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  return (
    <Box>
      {/* A NATIVE select. MUI's own renders a div with a hidden input, which
          on a phone replaces the platform's wheel picker with a popup list and
          in a test hands a lookup by label the hidden input rather than the
          control. The platform picker is the better control on the device most
          payers use. */}
      <TextInput
        select
        fullWidth
        id={id}
        label={label}
        value={value}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        variant="outlined"
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </TextInput>
    </Box>
  );
};

/**
 * Journeys B and C enter here: the payer browses the school's catalogue to the
 * placement their student is in (or is being registered into) and the opaque
 * `selection_id` that identifies it becomes the journey's anchor.
 *
 * ⛔ There is no search box and no doorway from a search result into this
 * screen (D-25, D-02). The registry decided which journey this service has,
 * once, and nothing here can move a payer to another one.
 *
 * ⛔ `selection_id` is never decoded. It is minted by the server, forwarded
 * exactly as given, and resolved server-side within the subsidiary the service
 * belongs to.
 */
const CatalogueBrowser = ({ coreServiceId }: { coreServiceId: string }) => {
  const { t, i18n } = useTranslation();
  const token = getStorageToken();
  const { selectionId, setSelectionId, setSelectionClass, selectFee } =
    useSchoolFee();

  const [page, setPage] = useState(1);
  const [placements, setPlacements] = useState<SchoolFeeCataloguePlacement[]>(
    []
  );
  const [exhausted, setExhausted] = useState(false);
  const [overflowed, setOverflowed] = useState(false);

  // Which pages have already been folded in. React may render an effect's
  // dependencies twice, and a page counted twice doubles a school's classes.
  const absorbed = useRef<Set<number>>(new Set());

  const catalogue = useSchoolFeeCatalogue({
    coreServiceId,
    page,
    size: CATALOGUE_PAGE_SIZE,
    authorisation: token || "",
    enabled: !exhausted && !overflowed,
  });

  const received = catalogue.data;

  useEffect(() => {
    if (!received) return;

    // ⛔ The page WE ASKED FOR, never the one the response reports. A server
    // that echoes `currentPage` wrongly -- or omits it -- would repeat a value
    // the guard below has already absorbed, so the pager would stop advancing
    // without ever setting `exhausted`, and the payer would sit on a spinner
    // for ever with no error to see. The requested page is ours, it always
    // increments, and the loop bound terminates it.
    const forPage = page;

    if (absorbed.current.has(forPage)) return;
    absorbed.current.add(forPage);

    const rows = received.items ?? [];

    setPlacements(current => [...current, ...rows]);

    // ⛔ Q4 condition 1. The pager stops on an EMPTY page or on the reported
    // total, and on nothing else. It must NOT stop on "fewer rows than I asked
    // for": the server clamps `size` to 100 without saying so, so a short page
    // is the normal case and a pager that treats it as the end truncates
    // almost every catalogue.
    if (rows.length === 0) {
      setExhausted(true);
      return;
    }

    const total = typeof received.total === "number" ? received.total : 0;

    if (total > 0 && placements.length + rows.length >= total) {
      setExhausted(true);
      return;
    }

    // Q4 condition 2: bound the loop, and refuse VISIBLY rather than render a
    // cascade missing the classes it never fetched.
    if (forPage >= MAX_CATALOGUE_PAGES) {
      setOverflowed(true);
      return;
    }

    setPage(forPage + 1);
  }, [received, page, placements.length]);

  const [period, setPeriod] = useState("");
  const [course, setCourse] = useState("");
  const [level, setLevel] = useState("");
  // A class chosen earlier in this journey, put back once the catalogue is
  // in, so returning to edit the details does not lose it.
  const [restoring, setRestoring] = useState(selectionId);

  // Academic period → Course → Level, always, and grouped on the display names
  // the catalogue returns. Two placements whose names collide are grouped
  // together and one whose name differs by a space is split -- the accepted
  // limitation of grouping on labels rather than on ids the response omits.
  const periods = useMemo(
    () => uniqueInOrder(placements.map(placement => placement.period_label)),
    [placements]
  );

  const courses = useMemo(
    () =>
      uniqueInOrder(
        placements
          .filter(placement => placement.period_label === period)
          .map(placement => placement.course_name)
      ),
    [placements, period]
  );

  const levels = useMemo(
    () =>
      uniqueInOrder(
        placements
          .filter(
            placement =>
              placement.period_label === period &&
              placement.course_name === course
          )
          .map(placement => placement.level_name)
      ),
    [placements, period, course]
  );

  // ⛔ Nothing is drawn or chosen until every page is in. Measured: with page 1
  // alone the catalogue held a single academic period, so the "one option is
  // not a choice" rule below took it -- and page 2 then added a second period
  // the payer had already been moved past without ever seeing it. A cascade
  // built over a partially loaded list is the same truncation Q4's conditions
  // exist to prevent, arriving by a different route.
  const settled = exhausted || overflowed;

  useEffect(() => {
    if (!settled || restoring === "") return;
    const earlier = placements.find(
      placement => placement.selection_id === restoring
    );
    if (earlier) {
      setPeriod(earlier.period_label);
      setCourse(earlier.course_name);
      setLevel(earlier.level_name);
    }
    setRestoring("");
  }, [settled, restoring, placements]);

  // A rung with exactly one option is not a choice, so it is taken rather than
  // shown as a select the payer must operate to proceed.
  useEffect(() => {
    if (!settled) return;
    if (periods.length === 1 && period !== periods[0]) setPeriod(periods[0]);
  }, [settled, periods, period]);

  useEffect(() => {
    if (!settled) return;
    if (courses.length === 1 && course !== courses[0]) setCourse(courses[0]);
  }, [settled, courses, course]);

  useEffect(() => {
    if (!settled) return;
    if (levels.length === 1 && level !== levels[0]) setLevel(levels[0]);
  }, [settled, levels, level]);

  const chosen =
    placements.find(
      placement =>
        placement.period_label === period &&
        placement.course_name === course &&
        placement.level_name === level
    ) ?? null;

  // Changing the placement drops the fee with it: a fee assignment belongs to
  // one offering, and carrying one across would pay another class's fee.
  useEffect(() => {
    const resolved = chosen?.selection_id ?? "";

    if (restoring !== "" || resolved === selectionId) return;

    setSelectionId(resolved);
    selectFee(null);
  }, [chosen, restoring, selectionId, setSelectionId, selectFee]);

  // Names what is chosen, restored or not, so Review can show the class.
  useEffect(() => {
    setSelectionClass(
      chosen ? [chosen.course_name, chosen.level_name].join(" · ") : ""
    );
  }, [chosen, setSelectionClass]);

  // Loading until the whole catalogue is in, not until the first page is.
  const loading = !settled && !catalogue.isError;

  return (
    <Box data-testid="school-fee-catalogue">
      <FormSection title={t("schoolfee.catalogue.title")}>

        {catalogue.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {getFailureDisplay(catalogue.error, t, i18n.language).message}
          </Alert>
        )}

        {loading && <Loading />}

        {/* Refused, not truncated. The cascade is not drawn at all, because a
            cascade a payer can operate is a cascade a payer will trust. */}
        {overflowed && (
          <Box data-testid="school-fee-catalogue-overflow">
            <Alert severity="error">
              <Typography variant="body1" fontWeight="700">
                {t("schoolfee.catalogue.too_many_title")}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t("schoolfee.catalogue.too_many_body")}
              </Typography>
            </Alert>
          </Box>
        )}

        {settled && !overflowed && placements.length === 0 && (
          <Box data-testid="school-fee-catalogue-empty">
            <Typography variant="body1" fontWeight="700">
              {t("schoolfee.catalogue.empty_title")}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {t("schoolfee.catalogue.empty_body")}
            </Typography>
          </Box>
        )}

        {settled && !overflowed && placements.length > 0 && (
          // Period, course and level side by side from a tablet up, stacked on
          // a phone, in the order the payer settles them.
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            }}
          >
            <PlacementSelect
              id="schoolfee-period"
              label={t("schoolfee.catalogue.period_label")}
              placeholder={t("schoolfee.catalogue.choose")}
              value={period}
              options={periods}
              onChange={value => {
                setPeriod(value);
                setCourse("");
                setLevel("");
              }}
            />

            {/* The course appears only once a period is set, so the payer
                never chooses a course that belongs to another year. */}
            {period !== "" && (
              <PlacementSelect
                id="schoolfee-course"
                label={t("schoolfee.catalogue.course_label")}
                placeholder={t("schoolfee.catalogue.choose")}
                value={course}
                options={courses}
                onChange={value => {
                  setCourse(value);
                  setLevel("");
                }}
              />
            )}

            {/* The level is in view from the start but closed until a course
                is chosen, so the order of the two is plain to the payer. */}
            {period !== "" && (
              <PlacementSelect
                id="schoolfee-level"
                label={t("schoolfee.catalogue.level_label")}
                placeholder={t("schoolfee.catalogue.choose")}
                value={level}
                options={course !== "" ? levels : []}
                onChange={setLevel}
                disabled={course === ""}
              />
            )}
          </Box>
        )}
      </FormSection>
    </Box>
  );
};

export default CatalogueBrowser;
